/**
 * AgentVault API client
 * All calls go to the FastAPI backend (default: http://localhost:8000)
 */
import axios from 'axios'
import type {
  AnalysisResult,
  AgentStatusResponse,
  OptimizeResult,
  RiskAssessmentResponse,
  MarketSnapshot,
  PortfolioSnapshot,
  EncryptionDemoResponse,
  OrchestratorRequest,
  OrchestratorResponse,
  HistoryPoint,
  UserProfile,
  ChatMessage,
  ChatResponse,
  PortfolioAnalytics,
  Account,
  Holding,
  HouseholdSummary,
  CSVImportResponse,
  Recommendation,
  Consensus,
  ExecutedTrade,
  TradeStats,
  PotState,
  Position,
  ClosedPosition,
  PositionSummary,
  PnlHistoryPoint,
  BacktestConfig,
  BacktestResult,
  BacktestStrategy,
  BacktestPreset,
  AllocationBreakdown,
  ConcentrationAnalysis,
  CorrelationMatrix,
  PerformanceData,
  RiskDecomposition,
  DividendSchedule,
  BenchmarkComparison,
  FactorExposureResult,
  GoalRecord,
  MonteCarloResult,
  GoalsSummary,
  WatchlistItem,
  AlertCondition,
  TriggeredAlert,
  RiskDashboard,
  StressTestResponse,
  LimitsResponse,
  ExposureResponse,
  CalendarEvent,
  TerminalResponse,
  PlatformSettings,
  AgentSettings,
  TradingSettings,
  HeatmapData,
  IndexData,
  MarketSessionsResponse,
  ScreenerFilters,
  ScreenerRunKickoff,
  ScreenerStoredResult,
  ScreenerPreset,
  StockRating,
  StockRatingsResponse,
  OHLCVResponse,
  TechnicalsResponse,
  IndicatorsResponse,
  CompareResponse,
  TelegramStatus,
  DiscordStatus,
  NewsFeedItem,
  SectorPerformanceResponse,
  SectorRotationResponse,
  SectorTopStocksResponse,
  SectorCorrelationResponse,
  MonthlyReport,
  FundSummary,
  PerformanceAttribution,
  JournalEntry,
  JournalEntryCreate,
  JournalTagCount,
  JournalAnalytics,
  CryptoDashboard,
  CryptoCoinDetail,
  CryptoDefiResponse,
  CryptoExchangesResponse,
  WhaleActivityResponse,
  CryptoCorrelationResponse,
  EarningsCalendarEntry,
  EarningsHistoryResponse,
  EarningsSurpriseResponse,
  EarningsWhisperResponse,
  EarningsMoversResponse,
  MacroDashboard,
  MacroRatesResponse,
  MacroInflationResponse,
  MacroLeadingIndicators,
  MonetaryPolicyResponse,
  StreetSentimentResponse,
  EconomicSurpriseResponse,
  MacroHeatMapResponse,
  MarkowitzResult,
  FrontierResult,
  RiskParityResult,
  RebalanceResult,
  CurrentVsOptimal,
  CorrelationMatrixResult,
  CorrelatedPairsResult,
  PairDivergenceResult,
  SectorCorrelationResult,
  SplitsResponse,
  CorporateDividend,
  UKEconomyDashboard,
  UKLeadingIndicatorsResponse,
  UKRatesResponse,
  GlobalRankingResponse,
  GroupSnapshotResponse,
  UpcomingIPO,
  RecentIPO,
  RecentIPOResponse,
  IPOPerformanceResponse,
  DividendPortfolioResponse,
  BacklogTask,
  BacklogTaskCreate,
  BacklogTaskUpdate,
  ForexDashboard,
  ForexPairDetail,
  ForexCrossRates,
  CommoditiesDashboard,
  OptionsChainResponse,
  ExpirationsResponse,
  MaxPainResponse,
  UnusualActivityResponse,
  IVRankResponse,
  DividendCalendarResponse,
  DividendHistoryResponse,
  DividendAristocratsResponse,
  TopYieldResponse,
  TaxWrapperSummary,
  CgtEstimate,
  TaxAllowances,
  IncomeSummaryResponse,
  DiscretionaryCurrentResponse,
  DiscretionarySuggestionsResponse,
  WhatIfRequest,
  WhatIfResponse,
  StrategyRecommendationsResponse,
  PortfolioHealthResponse,
  ModelPortfoliosResponse,
  TargetAllocationResponse,
  LivePnlResponse,
  FeeSummary,
  FeeTerAnalysis,
  AllowanceStatus,
  CgtPlanner,
  PriceShockResponse,
  ModelComparisonResponse,
  WhatWouldResponse,
  WhatWouldAllResponse,
  RiskCorrelationMatrix,
} from './types'

const API_URL = import.meta.env.VITE_API_URL ?? ''

export const api = axios.create({
  baseURL: API_URL,
  timeout: 60_000,
  headers: { 'Content-Type': 'application/json' },
})

// ── Retry interceptor: retry once on timeout ─────────────────────────────
// GET/HEAD only — auto-retrying a timed-out POST/PUT/DELETE risks executing
// a non-idempotent action twice (journal create, order placement): the first
// request may still complete server-side after the client gives up.
api.interceptors.response.use(undefined, async error => {
  const config = error?.config
  const method = String(config?.method ?? 'get').toLowerCase()
  if (
    config &&
    !config.__retried &&
    (method === 'get' || method === 'head') &&
    error?.code === 'ECONNABORTED' &&
    error?.message?.includes('timeout')
  ) {
    config.__retried = true
    return api.request(config)
  }
  return Promise.reject(error)
})

// ── Error-detail normaliser ──────────────────────────────────────────────────
// FastAPI `detail` can be a string, a dict (e.g. 422 {"errors": [...]}), or a
// list of pydantic error objects. Rendering a non-string as a React child
// crashes the page — always pass details through here first.
export function normalizeApiErrorDetail(detail: unknown, fallback: string): string {
  if (detail == null) return fallback
  if (typeof detail === 'string') return detail || fallback
  if (Array.isArray(detail)) {
    const parts = detail
      .map(d => (typeof d === 'string' ? d : (d as { msg?: string })?.msg ?? JSON.stringify(d)))
      .filter(Boolean)
    return parts.length ? parts.join('; ') : fallback
  }
  if (typeof detail === 'object') {
    const errs = (detail as { errors?: unknown }).errors
    if (Array.isArray(errs) && errs.length) {
      return errs.map(e => (typeof e === 'string' ? e : JSON.stringify(e))).join('; ')
    }
    const msg = (detail as { message?: unknown; error?: unknown }).message
      ?? (detail as { error?: unknown }).error
    if (typeof msg === 'string' && msg) return msg
    try {
      return JSON.stringify(detail)
    } catch {
      return fallback
    }
  }
  return String(detail)
}

// ── Execution-result formatter ───────────────────────────────────────────────
// rebalance-queue `execution_result` is Optional[dict] (Alpaca order payload
// or {error}). Reduce it to a short human string for table cells.
export function formatExecutionResult(
  result: { order_id?: unknown; error?: unknown; [k: string]: unknown } | string | null | undefined,
): string {
  if (result == null) return '-'
  if (typeof result === 'string') return result || '-'
  if (typeof result !== 'object') return String(result)
  if (result.error != null && result.error !== '') return `Error: ${String(result.error)}`
  if (result.order_id != null && result.order_id !== '') return `Order ${String(result.order_id)}`
  try {
    const s = JSON.stringify(result)
    return s === '{}' ? '-' : s
  } catch {
    return '-'
  }
}

// ── Auth interceptors ─────────────────────────────────────────────────────

// Request: attach JWT from localStorage
api.interceptors.request.use(config => {
  const token = localStorage.getItem('av-access-token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response: redirect to /login on 401 (but not for auth endpoints themselves)
api.interceptors.response.use(
  res => res,
  error => {
    if (
      error?.response?.status === 401 &&
      !error?.config?.url?.includes('/api/auth/')
    ) {
      localStorage.removeItem('av-access-token')
      // Clear persisted auth state
      try {
        const stored = localStorage.getItem('av-auth')
        if (stored) {
          const parsed = JSON.parse(stored)
          parsed.state = { ...parsed.state, isAuthenticated: false, user: null, accessToken: null }
          localStorage.setItem('av-auth', JSON.stringify(parsed))
        }
      } catch { /* ignore */ }
      window.location.href = '/login'
    }
    return Promise.reject(error)
  },
)

// ── Authenticated CSV download ────────────────────────────────────────────────
// A plain <a href download> can't carry the Bearer token (auth is via the
// Authorization header, not a cookie), so auth-gated CSV endpoints 401 on a
// raw link. Fetch the blob through the authed axios instance, then trigger a
// client-side download. Returns {ok, error} so callers can surface failures
// instead of a silent no-op. (Truthy on success, so legacy boolean checks
// still behave.)
export async function downloadCsv(
  path: string,
  filename: string,
): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await api.get(path, { responseType: 'blob' })
    const url = window.URL.createObjectURL(res.data as Blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
    return { ok: true }
  } catch (e: any) {
    const detail = e?.response?.data?.detail
    return {
      ok: false,
      error: normalizeApiErrorDetail(detail, e?.message ?? 'Download failed'),
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Health
// ─────────────────────────────────────────────────────────────────────────────

export async function getHealth(): Promise<{ status: string }> {
  const { data } = await api.get('/api/health')
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Agents
// ─────────────────────────────────────────────────────────────────────────────

export async function getAgentStatus(): Promise<AgentStatusResponse> {
  const { data } = await api.get('/api/agents/status')
  return data
}

export async function postAgentAnalyze(
  userProfile: UserProfile,
  snapshot: PortfolioSnapshot,
): Promise<AnalysisResult> {
  const { data } = await api.post('/api/agents/analyze', {
    user_profile: userProfile,
    snapshot,
  })
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Portfolio
// ─────────────────────────────────────────────────────────────────────────────

export async function getPortfolioSnapshot(
  userId = 'unman_lai_001',
): Promise<PortfolioSnapshot> {
  const { data } = await api.get('/api/portfolio/snapshot', {
    params: { user_id: userId },
  })
  return data
}

export async function getPortfolioHistory(
  userId = 'unman_lai_001',
  days = 30,
): Promise<HistoryPoint[]> {
  const { data } = await api.get('/api/portfolio/history', {
    params: { user_id: userId, days },
  })
  return data
}

export async function postOptimizePortfolio(
  userProfile: UserProfile,
  snapshot: PortfolioSnapshot,
): Promise<OptimizeResult> {
  const { data } = await api.post('/api/portfolio/optimize', {
    user_profile: userProfile,
    snapshot,
  })
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Risk
// ─────────────────────────────────────────────────────────────────────────────

export async function postRiskAssess(
  userProfile: UserProfile,
  snapshot: PortfolioSnapshot,
): Promise<RiskAssessmentResponse> {
  const { data } = await api.post('/api/risk/assess', {
    user_profile: userProfile,
    snapshot,
  })
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Market
// ─────────────────────────────────────────────────────────────────────────────

export async function getMarketIntelligence(): Promise<MarketSnapshot> {
  const { data } = await api.get('/api/market/intelligence')
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Encryption
// ─────────────────────────────────────────────────────────────────────────────

export async function getEncryptionDemo(): Promise<EncryptionDemoResponse> {
  const { data } = await api.get('/api/encryption/demo')
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Chat
// ─────────────────────────────────────────────────────────────────────────────

export async function postChat(
  message: string,
  history: ChatMessage[],
  userId = 'unman_lai_001',
): Promise<ChatResponse> {
  const { data } = await api.post('/api/chat', {
    message,
    history: history.map(m => ({ role: m.role, content: m.content })),
    user_id: userId,
  })
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Portfolio analytics
// ─────────────────────────────────────────────────────────────────────────────

export async function getPortfolioAnalytics(
  userId = 'unman_lai_001',
): Promise<PortfolioAnalytics> {
  const { data } = await api.get('/api/portfolio/analytics', {
    params: { user_id: userId },
  })
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Portfolio deep analytics
// ─────────────────────────────────────────────────────────────────────────────

export async function getPortfolioAllocation(userId = 'unman_lai_001'): Promise<AllocationBreakdown> {
  const { data } = await api.get('/api/portfolio/allocation', { params: { user_id: userId } })
  return data
}

export async function getPortfolioConcentration(userId = 'unman_lai_001'): Promise<ConcentrationAnalysis> {
  const { data } = await api.get('/api/portfolio/concentration', { params: { user_id: userId } })
  return data
}

export async function getPortfolioCorrelation(userId = 'unman_lai_001'): Promise<CorrelationMatrix> {
  const { data } = await api.get('/api/portfolio/correlation', { params: { user_id: userId } })
  return data
}

export async function getPortfolioPerformance(userId = 'unman_lai_001', days = 90): Promise<PerformanceData> {
  const { data } = await api.get('/api/portfolio/performance', { params: { user_id: userId, days } })
  return data
}

export async function getPortfolioRiskDecomposition(userId = 'unman_lai_001'): Promise<RiskDecomposition> {
  const { data } = await api.get('/api/portfolio/risk-decomposition', { params: { user_id: userId } })
  return data
}

export async function getFxExposure(): Promise<import('./types').FxExposure> {
  const { data } = await api.get('/api/pnl/fx-exposure')
  return data
}

export async function getFxSensitivity(): Promise<import('./types').FxSensitivity> {
  const { data } = await api.get('/api/net-worth/fx-sensitivity')
  return data
}

export async function getPortfolioDividendSchedule(userId = 'unman_lai_001'): Promise<DividendSchedule> {
  const { data } = await api.get('/api/portfolio/dividend-schedule', { params: { user_id: userId } })
  return data
}

export async function getPortfolioBenchmark(
  benchmarks = 'SPY,QQQ,IWM',
  period = '1y',
  userId = 'unman_lai_001',
): Promise<BenchmarkComparison> {
  const { data } = await api.get('/api/portfolio/benchmark', {
    params: { benchmarks, period, user_id: userId },
  })
  return data
}

export async function getPortfolioFactorExposure(userId = 'unman_lai_001'): Promise<FactorExposureResult> {
  const { data } = await api.get('/api/portfolio/factor-exposure', { params: { user_id: userId } })
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Accounts
// ─────────────────────────────────────────────────────────────────────────────

export async function getAccounts(): Promise<Account[]> {
  const { data } = await api.get('/api/accounts')
  return data
}

export async function createAccount(body: {
  name: string; provider: string; account_type: string;
  currency?: string; owner?: string; notes?: string; discretionary?: boolean;
}): Promise<Account> {
  const { data } = await api.post('/api/accounts', body)
  return data
}

export async function updateAccount(id: number, body: Record<string, unknown>): Promise<Account> {
  const { data } = await api.put(`/api/accounts/${id}`, body)
  return data
}

export async function deleteAccount(id: number): Promise<void> {
  await api.delete(`/api/accounts/${id}`)
}

export async function getHoldings(accountId: number): Promise<Holding[]> {
  const { data } = await api.get(`/api/accounts/${accountId}/holdings`)
  return data
}

export async function addHolding(
  accountId: number,
  body: { name: string; quantity: number; value: number; [k: string]: unknown },
): Promise<Holding> {
  const { data } = await api.post(`/api/accounts/${accountId}/holdings`, body)
  return data
}

export async function updateHolding(
  accountId: number, holdingId: number, body: Record<string, unknown>,
): Promise<Holding> {
  const { data } = await api.put(`/api/accounts/${accountId}/holdings/${holdingId}`, body)
  return data
}

export async function deleteHolding(accountId: number, holdingId: number): Promise<void> {
  await api.delete(`/api/accounts/${accountId}/holdings/${holdingId}`)
}

export async function importCSV(
  accountId: number,
  file: File,
  opts: { dryRun?: boolean; merge?: boolean; autoResolveIsins?: boolean } = {},
): Promise<CSVImportResponse> {
  const form = new FormData()
  form.append('file', file)
  const params = new URLSearchParams()
  if (opts.dryRun) params.set('dry_run', 'true')
  if (opts.merge) params.set('merge', 'true')
  if (opts.autoResolveIsins === false) params.set('auto_resolve_isins', 'false')
  const url = `/api/accounts/${accountId}/import-csv${params.toString() ? `?${params}` : ''}`
  const { data } = await api.post(url, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function getHouseholdSummary(): Promise<HouseholdSummary> {
  const { data } = await api.get('/api/accounts/summary')
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Recommendations
// ─────────────────────────────────────────────────────────────────────────────

export async function getRecommendations(status?: string): Promise<Recommendation[]> {
  const params: Record<string, string> = {}
  if (status) params.status = status
  const { data } = await api.get('/api/recommendations', { params })
  return data
}

export async function getPendingRecommendations(): Promise<Recommendation[]> {
  const { data } = await api.get('/api/recommendations/pending')
  return data
}

export async function approveRecommendation(id: number): Promise<Recommendation> {
  const { data } = await api.post(`/api/recommendations/${id}/approve`)
  return data
}

export async function rejectRecommendation(id: number, reason?: string): Promise<Recommendation> {
  const { data } = await api.post(`/api/recommendations/${id}/reject`, { reason })
  return data
}

export async function getRecommendationConsensus(id: number): Promise<Consensus> {
  const { data } = await api.get(`/api/recommendations/${id}/consensus`)
  return data
}

export async function createRecommendation(body: {
  action: 'BUY' | 'SELL'
  ticker: string
  quantity: number
  title: string
  reasoning: string
  confidence?: number
  priority?: number
}): Promise<Recommendation> {
  const { data } = await api.post('/api/recommendations', body)
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Trades (execution pipeline)
// ─────────────────────────────────────────────────────────────────────────────

export async function getExecutedTrades(): Promise<ExecutedTrade[]> {
  const { data } = await api.get('/api/trades')
  return data
}

export async function getTradeStats(): Promise<TradeStats> {
  const { data } = await api.get('/api/trades/stats')
  return data
}

export async function getPotState(): Promise<PotState> {
  const { data } = await api.get('/api/trades/pot')
  return data
}

export async function simulateTrade(body: {
  ticker: string; action: string; size_gbp: number;
}): Promise<ExecutedTrade> {
  const { data } = await api.post('/api/trades/simulate', body)
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Positions
// ─────────────────────────────────────────────────────────────────────────────

export async function getPositions(): Promise<Position[]> {
  const { data } = await api.get('/api/positions')
  return data
}

export async function getPositionSummary(): Promise<PositionSummary> {
  const { data } = await api.get('/api/positions/summary')
  return data
}

export async function getClosedPositions(): Promise<ClosedPosition[]> {
  const { data } = await api.get('/api/positions/closed')
  return data
}

export async function getPnlHistory(): Promise<PnlHistoryPoint[]> {
  const { data } = await api.get('/api/positions/pnl-history')
  return data
}

export async function addPosition(body: {
  ticker: string; name?: string; action: string; entry_price: number;
  quantity: number; stop_loss?: number; take_profit?: number; notes?: string;
}): Promise<Position> {
  const { data } = await api.post('/api/positions', body)
  return data
}

export async function updatePosition(id: number, body: {
  stop_loss?: number; take_profit?: number; notes?: string;
}): Promise<Position> {
  const { data } = await api.put(`/api/positions/${id}`, body)
  return data
}

export async function closePosition(id: number, body?: {
  exit_price?: number; notes?: string;
}): Promise<ClosedPosition> {
  const { data } = await api.post(`/api/positions/${id}/close`, body ?? {})
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Alternative Data
// ─────────────────────────────────────────────────────────────────────────────

export async function getCongressRecent(days = 30) {
  const { data } = await api.get('/api/alt-data/congress/recent', { params: { days } })
  return data
}

export async function getCongressTopBought(days = 30, limit = 20) {
  const { data } = await api.get('/api/alt-data/congress/top-bought', { params: { days, limit } })
  return data
}

export async function getCongressTopSold(days = 30, limit = 20) {
  const { data } = await api.get('/api/alt-data/congress/top-sold', { params: { days, limit } })
  return data
}

export async function getInsiderRecent(days = 7) {
  const { data } = await api.get('/api/alt-data/insider/recent', { params: { days } })
  return data
}

export async function getInsiderClusters(days = 30) {
  const { data } = await api.get('/api/alt-data/insider/clusters', { params: { days } })
  return data
}

export async function getInsiderLarge(minValue = 100_000, days = 30) {
  const { data } = await api.get('/api/alt-data/insider/large', { params: { min_value: minValue, days } })
  return data
}

export async function getGuruPortfolios() {
  const { data } = await api.get('/api/alt-data/institutional/gurus')
  return data
}

export async function getWhaleMoves(minValue = 10_000_000) {
  const { data } = await api.get('/api/alt-data/institutional/whale-moves', { params: { min_value: minValue } })
  return data
}

export async function getGovContracts(days = 30, minValue = 1_000_000) {
  const { data } = await api.get('/api/alt-data/contracts/recent', { params: { days, min_value: minValue } })
  return data
}

export async function getDefenseContracts(days = 30) {
  const { data } = await api.get('/api/alt-data/contracts/defense', { params: { days } })
  return data
}

export async function getFedIndicators() {
  const { data } = await api.get('/api/alt-data/fed/indicators')
  return data
}

export async function getYieldCurve() {
  const { data } = await api.get('/api/alt-data/fed/yield-curve')
  return data
}

export async function getRecessionSignals() {
  const { data } = await api.get('/api/alt-data/fed/recession-signals')
  return data
}

export async function getFedCalendar() {
  const { data } = await api.get('/api/alt-data/fed/calendar')
  return data
}

export async function getFearGreed() {
  const { data } = await api.get('/api/alt-data/sentiment/fear-greed')
  return data
}

export async function getWsbTrending(limit = 25) {
  const { data } = await api.get('/api/alt-data/sentiment/wsb', { params: { limit } })
  return data
}

export async function getTrendingTickers(limit = 20) {
  const { data } = await api.get('/api/alt-data/sentiment/trending', { params: { limit } })
  return data
}

export async function getNewsHeadlines(limit = 20) {
  const { data } = await api.get('/api/alt-data/news/headlines', { params: { limit } })
  return data
}

export async function getMaterialEvents(days = 7) {
  const { data } = await api.get('/api/alt-data/news/events', { params: { days } })
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Short Interest
// ─────────────────────────────────────────────────────────────────────────────

export async function getMostShorted(limit = 30) {
  const { data } = await api.get('/api/alt-data/shorts/most-shorted', { params: { limit } })
  return data
}

export async function getShortInterestTicker(ticker: string) {
  const { data } = await api.get(`/api/alt-data/shorts/ticker/${ticker}`)
  return data
}

export async function getSqueezeCandidates() {
  const { data } = await api.get('/api/alt-data/shorts/squeeze-candidates')
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// ETF Flows
// ─────────────────────────────────────────────────────────────────────────────

export async function getETFInflows(days = 7) {
  const { data } = await api.get('/api/alt-data/etf/inflows', { params: { days } })
  return data
}

export async function getETFOutflows(days = 7) {
  const { data } = await api.get('/api/alt-data/etf/outflows', { params: { days } })
  return data
}

export async function getSectorRotation() {
  const { data } = await api.get('/api/alt-data/etf/sector-rotation')
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Lobbying
// ─────────────────────────────────────────────────────────────────────────────

export async function getLobbyingTopSpenders(limit = 20) {
  const { data } = await api.get('/api/alt-data/lobbying/top-spenders', { params: { limit } })
  return data
}

export async function getLobbyingByCompany(company: string) {
  const { data } = await api.get(`/api/alt-data/lobbying/company/${company}`)
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Patents
// ─────────────────────────────────────────────────────────────────────────────

export async function getRecentPatents(days = 30) {
  const { data } = await api.get('/api/alt-data/patents/recent', { params: { days } })
  return data
}

export async function getPatentsByCompany(company: string) {
  const { data } = await api.get(`/api/alt-data/patents/company/${company}`)
  return data
}

export async function getTopInnovators(limit = 20) {
  const { data } = await api.get('/api/alt-data/patents/top-innovators', { params: { limit } })
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Corporate Flights
// ─────────────────────────────────────────────────────────────────────────────

export async function getRecentFlights(hours = 24) {
  const { data } = await api.get('/api/alt-data/flights/recent', { params: { hours } })
  return data
}

export async function getUnusualFlights() {
  const { data } = await api.get('/api/alt-data/flights/unusual')
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Options Flow
// ─────────────────────────────────────────────────────────────────────────────

export async function getUnusualOptions(minPremium = 50_000) {
  const { data } = await api.get('/api/alt-data/options/unusual', { params: { min_premium: minPremium } })
  return data
}

export async function getPutCallRatio() {
  const { data } = await api.get('/api/alt-data/options/put-call-ratio')
  return data
}

export async function getTickerOptionsFlow(ticker: string) {
  const { data } = await api.get(`/api/alt-data/options/flow/${ticker}`)
  return data
}

export async function getMaxPain(ticker: string) {
  const { data } = await api.get(`/api/alt-data/options/max-pain/${ticker}`)
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Dark Pool
// ─────────────────────────────────────────────────────────────────────────────

export async function getDarkPoolVolume(ticker: string) {
  const { data } = await api.get(`/api/alt-data/dark-pool/volume/${ticker}`)
  return data
}

export async function getMostActiveDarkPool() {
  const { data } = await api.get('/api/alt-data/dark-pool/most-active')
  return data
}

export async function getDarkPoolAccumulation(ticker: string) {
  const { data } = await api.get(`/api/alt-data/dark-pool/accumulation/${ticker}`)
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Whale Alerts
// ─────────────────────────────────────────────────────────────────────────────

export async function getWhaleLargeMoves(minUsd = 1_000_000) {
  const { data } = await api.get('/api/alt-data/whale/large-moves', { params: { min_usd: minUsd } })
  return data
}

export async function getWhaleExchangeFlows() {
  const { data } = await api.get('/api/alt-data/whale/exchange-flows')
  return data
}

// Congress member search
export async function getCongressByMember(name: string) {
  const { data } = await api.get(`/api/alt-data/congress/member/${encodeURIComponent(name)}`)
  return data
}

// Google Trends
export async function getTrendingSearches(geo = 'united_states') {
  const { data } = await api.get('/api/alt-data/trends/trending', { params: { geo } })
  return data
}

export async function getTrendInterest(keyword: string, timeframe = 'today 3-m') {
  const { data } = await api.get(`/api/alt-data/trends/interest/${encodeURIComponent(keyword)}`, { params: { timeframe } })
  return data
}

// Jim Cramer Tracker
export async function getCramerPicks(limit = 50) {
  const { data } = await api.get('/api/alt-data/cramer/picks', { params: { limit } })
  return data
}

// Stock Splits
export async function getRecentSplits(days = 90) {
  const { data } = await api.get('/api/alt-data/splits/recent', { params: { days } })
  return data
}

export async function getUpcomingSplits() {
  const { data } = await api.get('/api/alt-data/splits/upcoming')
  return data
}

// Risk Factors
export async function getRiskFactors(ticker: string) {
  const { data } = await api.get(`/api/alt-data/risk-factors/${ticker}`)
  return data
}

// Executive Compensation
export async function getExecComp(ticker: string) {
  const { data } = await api.get(`/api/alt-data/exec-comp/${ticker}`)
  return data
}

// Revenue Breakdown
export async function getRevenueBreakdown(ticker: string) {
  const { data } = await api.get(`/api/alt-data/revenue-breakdown/${ticker}`)
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Backtesting
// ─────────────────────────────────────────────────────────────────────────────

export async function runBacktest(config: BacktestConfig): Promise<BacktestResult> {
  const { data } = await api.post('/api/backtest/run', config, { timeout: 120_000 })
  return data
}

export async function getBacktestStrategies(): Promise<{ strategies: BacktestStrategy[] }> {
  const { data } = await api.get('/api/backtest/strategies')
  return data
}

export async function getBacktestPresets(): Promise<{ presets: BacktestPreset[] }> {
  const { data } = await api.get('/api/backtest/presets')
  return data
}

// ── New Backtest endpoints (added 2026-04-25) ───────────────────────────────

export interface PortfolioBacktestResult {
  period: string
  portfolio_ret_pct: number
  coverage_pct: number
  tracked_value_gbp: number
  per_ticker: Array<{
    symbol: string; weight: number;
    ret_pct: number | null; contribution_pct: number | null;
    error?: string;
  }>
  benchmarks: Array<{
    symbol: string; ret_pct: number | null;
    edge_pct?: number; error?: string;
  }>
  skipped_no_symbol_count: number
  note?: string
}

export async function backtestPortfolio(
  period: string = '1y',
  benchmarks: string = 'SPY,QQQ,VUAG.L',
): Promise<PortfolioBacktestResult> {
  const { data } = await api.get(`/api/backtest/portfolio?period=${period}&benchmarks=${encodeURIComponent(benchmarks)}`, { timeout: 60_000 })
  return data
}

export interface StrategyCompareResult {
  ranked: Array<{
    strategy: string; name: string;
    total_return_pct: number; annualized_return: number;
    sharpe_ratio: number; max_drawdown_pct: number;
    win_rate: number; total_trades: number;
    profit_factor: number; final_value: number;
  }>
  failed: Array<{ strategy: string; error: string }>
  winner: StrategyCompareResult['ranked'][number] | null
}

export async function compareStrategies(config: BacktestConfig): Promise<StrategyCompareResult> {
  const { data } = await api.post('/api/backtest/compare-strategies', config, { timeout: 240_000 })
  return data
}

export interface BacktestOptimizeRow {
  stop_loss_pct: number; take_profit_pct: number; max_position_pct: number;
  total_return_pct: number; sharpe_ratio: number;
  max_drawdown_pct: number; win_rate: number; total_trades: number;
}

export interface BacktestOptimizeResult {
  combos_tested: number
  median_sharpe: number
  top: BacktestOptimizeRow[]
  winner: BacktestOptimizeRow | null
  failed: Array<{ error: string; [k: string]: unknown }>
}

export async function optimizeStrategy(config: BacktestConfig): Promise<BacktestOptimizeResult> {
  const { data } = await api.post('/api/backtest/optimize', config, { timeout: 600_000 })
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Watchlist & Alerts
// ─────────────────────────────────────────────────────────────────────────────

export async function getWatchlist(): Promise<WatchlistItem[]> {
  const { data } = await api.get('/api/watchlist')
  return data
}

export async function addToWatchlist(body: {
  ticker: string; name: string; notes?: string;
  alerts?: AlertCondition[];
}): Promise<WatchlistItem> {
  const { data } = await api.post('/api/watchlist', body)
  return data
}

export async function removeFromWatchlist(id: number): Promise<void> {
  await api.delete(`/api/watchlist/${id}`)
}

export async function updateWatchlistItem(
  id: number,
  body: { notes?: string; alerts?: AlertCondition[] },
): Promise<WatchlistItem> {
  const { data } = await api.put(`/api/watchlist/${id}`, body)
  return data
}

export async function getTriggeredAlerts(): Promise<TriggeredAlert[]> {
  const { data } = await api.get('/api/watchlist/alerts')
  return data
}

export async function dismissWatchlistAlert(id: number): Promise<void> {
  await api.post(`/api/watchlist/${id}/dismiss-alert`)
}

// ─────────────────────────────────────────────────────────────────────────────
// Orchestrator
// ─────────────────────────────────────────────────────────────────────────────

export async function postWealthOrchestrator(
  req: OrchestratorRequest,
): Promise<OrchestratorResponse> {
  const { data } = await api.post('/api/orchestrator', req)
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Goals & Monte Carlo
// ─────────────────────────────────────────────────────────────────────────────

export async function getGoals(): Promise<GoalRecord[]> {
  const { data } = await api.get('/api/goals')
  return data
}

export async function createGoal(body: {
  name: string; target_gbp: number; horizon_date: string; notes?: string;
}): Promise<GoalRecord> {
  const { data } = await api.post('/api/goals', body)
  return data
}

export async function updateGoal(id: number, body: Record<string, unknown>): Promise<GoalRecord> {
  const { data } = await api.put(`/api/goals/${id}`, body)
  return data
}

export async function deleteGoal(id: number): Promise<void> {
  await api.delete(`/api/goals/${id}`)
}

export async function simulateGoal(id: number, config?: {
  portfolio_value?: number;
  mean_annual_return?: number;
  annual_volatility?: number;
  num_simulations?: number;
  monthly_contribution?: number;
}): Promise<MonteCarloResult> {
  const { data } = await api.post(`/api/goals/${id}/simulate`, config ?? {})
  return data
}

export async function getGoalsSummary(): Promise<GoalsSummary> {
  const { data } = await api.get('/api/goals/summary')
  return data
}

// Ambition Check — honest feasibility of "£X more in Y months".
// All fields nullable: the backend never guarantees a value.
export interface AmbitionCheckResult {
  current_investable_gbp: number | null
  target_gain_gbp: number | null
  target_value_gbp: number | null
  horizon_months: number | null
  extra_monthly_contribution_gbp: number | null
  required_total_growth_pct: number | null
  required_cagr_pct: number | null
  probability_pct: number | null
  one_in_n: number | null
  percentile_outcomes: { p10?: number | null; p50?: number | null; p90?: number | null } | null
  honest_sentence: string | null
  caveat: string | null
  assumptions: {
    mean_annual_return_pct?: number | null
    annual_volatility_pct?: number | null
    assumption_source?: string | null
    simulations_run?: number | null
  } | null
}

export async function ambitionCheck(body: {
  target_gain_gbp: number
  horizon_months: number
  extra_monthly_contribution_gbp?: number
}): Promise<AmbitionCheckResult> {
  const { data } = await api.post('/api/goals/ambition-check', body)
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Notifications
// ─────────────────────────────────────────────────────────────────────────────

export async function getNotifications(limit = 50) {
  const { data } = await api.get('/api/notifications', { params: { limit } })
  return data
}

export async function getUnreadCount(): Promise<{ count: number }> {
  const { data } = await api.get('/api/notifications/unread')
  return data
}

export async function markNotificationRead(id: number) {
  const { data } = await api.post(`/api/notifications/${id}/read`)
  return data
}

export async function markAllNotificationsRead() {
  const { data } = await api.post('/api/notifications/read-all')
  return data
}

export async function deleteNotification(id: number) {
  const { data } = await api.delete(`/api/notifications/${id}`)
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Risk Dashboard
// ─────────────────────────────────────────────────────────────────────────────

export async function getRiskDashboard(): Promise<RiskDashboard> {
  const { data } = await api.get('/api/risk/dashboard')
  return data
}

export async function getRiskStressTest(): Promise<StressTestResponse> {
  const { data } = await api.get('/api/risk/stress-test')
  return data
}

export async function getRiskLimits(): Promise<LimitsResponse> {
  const { data } = await api.get('/api/risk/limits')
  return data
}

export async function getRiskExposure(): Promise<ExposureResponse> {
  const { data } = await api.get('/api/risk/exposure')
  return data
}

export async function getRiskCorrelation(): Promise<RiskCorrelationMatrix> {
  const { data } = await api.get('/api/risk/correlation', { timeout: 60_000 })
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Calendar
// ─────────────────────────────────────────────────────────────────────────────

export async function getCalendarEvents(days = 14): Promise<CalendarEvent[]> {
  const { data } = await api.get('/api/calendar/events', { params: { days } })
  return data
}

export async function getCalendarEarnings(days = 14): Promise<CalendarEvent[]> {
  const { data } = await api.get('/api/calendar/earnings', { params: { days } })
  return data
}

export async function getCalendarEconomic(days = 14): Promise<CalendarEvent[]> {
  const { data } = await api.get('/api/calendar/economic', { params: { days } })
  return data
}

export async function getCalendarExpiry(days = 30): Promise<CalendarEvent[]> {
  const { data } = await api.get('/api/calendar/expiry', { params: { days } })
  return data
}

export async function getCalendarDividends(days = 30): Promise<CalendarEvent[]> {
  const { data } = await api.get('/api/calendar/dividends', { params: { days } })
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Earnings Analysis
// ─────────────────────────────────────────────────────────────────────────────

export async function getEarningsCalendar(days = 14): Promise<EarningsCalendarEntry[]> {
  const { data } = await api.get('/api/earnings/calendar', { params: { days } })
  return data
}

export async function getEarningsHistory(ticker: string): Promise<EarningsHistoryResponse> {
  const { data } = await api.get(`/api/earnings/history/${ticker}`)
  return data
}

export async function getEarningsSurprise(ticker: string): Promise<EarningsSurpriseResponse> {
  const { data } = await api.get(`/api/earnings/surprise/${ticker}`)
  return data
}

export async function getEarningsWhisper(ticker: string): Promise<EarningsWhisperResponse> {
  const { data } = await api.get(`/api/earnings/whisper/${ticker}`)
  return data
}

export async function getEarningsMovers(): Promise<EarningsMoversResponse> {
  const { data } = await api.get('/api/earnings/movers')
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Terminal
// ─────────────────────────────────────────────────────────────────────────────

export async function executeTerminalCommand(command: string): Promise<TerminalResponse> {
  const { data } = await api.post('/api/terminal/command', { command })
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Intelligence / Brain
// ─────────────────────────────────────────────────────────────────────────────

export async function getIntelligenceAccuracy() {
  const { data } = await api.get('/api/intelligence/accuracy')
  return data
}

export async function getIntelligenceSignals() {
  const { data } = await api.get('/api/intelligence/signals')
  return data
}

export async function getIntelligencePatterns() {
  const { data } = await api.get('/api/intelligence/patterns')
  return data
}

export async function getIntelligenceMeta() {
  const { data } = await api.get('/api/intelligence/meta')
  return data
}

export async function getIntelligenceBrainStatus() {
  const { data } = await api.get('/api/intelligence/brain-status')
  return data
}

export async function getAgentSignals(tickers?: string) {
  const params = tickers ? `?tickers=${encodeURIComponent(tickers)}` : ''
  const { data } = await api.get(`/api/intelligence/agent-signals${params}`)
  return data
}

export async function recordTradeOutcome(body: {
  recommendation_id: number
  pnl_pct: number
  ticker?: string
  action?: string
  agents_who_agreed?: string[]
  agents_who_disagreed?: string[]
  signal_types?: string[]
  consensus_agreement_pct?: number
  regime_at_time?: string
}) {
  const { data } = await api.post('/api/intelligence/record-outcome', body)
  return data
}

export async function getLearningSummary() {
  const { data } = await api.get('/api/intelligence/learning-summary')
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Settings
// ─────────────────────────────────────────────────────────────────────────────

export async function getSettings(): Promise<PlatformSettings> {
  const { data } = await api.get('/api/settings')
  return data
}

export async function updateSettings(body: Partial<PlatformSettings>): Promise<PlatformSettings> {
  const { data } = await api.put('/api/settings', body)
  return data
}

export async function getAgentSettings(): Promise<AgentSettings> {
  const { data } = await api.get('/api/settings/agents')
  return data
}

export async function updateAgentSettings(body: Partial<AgentSettings>): Promise<AgentSettings> {
  const { data } = await api.put('/api/settings/agents', body)
  return data
}

export async function getPotRules(): Promise<TradingSettings> {
  const { data } = await api.get('/api/settings/pot-rules')
  return data
}

export async function updatePotRules(body: Partial<TradingSettings>): Promise<TradingSettings> {
  const { data } = await api.put('/api/settings/pot-rules', body)
  return data
}

export async function resetSettings(): Promise<{ ok: boolean; settings: PlatformSettings }> {
  const { data } = await api.post('/api/settings/reset')
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Telegram Notifications
// ─────────────────────────────────────────────────────────────────────────────

export async function getTelegramStatus(): Promise<TelegramStatus> {
  const { data } = await api.get('/api/notifications/telegram/status')
  return data
}

export async function updateTelegramSettings(body: Record<string, unknown>): Promise<TelegramStatus> {
  const { data } = await api.put('/api/notifications/telegram/settings', body)
  return data
}

export async function sendTelegramTest(): Promise<{ ok: boolean; error: string | null }> {
  const { data } = await api.post('/api/notifications/telegram/test')
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Discord Notifications
// ─────────────────────────────────────────────────────────────────────────────

export async function getDiscordStatus(): Promise<DiscordStatus> {
  const { data } = await api.get('/api/notifications/discord/status')
  return data
}

export async function updateDiscordSettings(body: Record<string, unknown>): Promise<DiscordStatus> {
  const { data } = await api.put('/api/notifications/discord/settings', body)
  return data
}

export async function sendDiscordTest(): Promise<{ ok: boolean; error: string | null }> {
  const { data } = await api.post('/api/notifications/discord/test')
  return data
}

export async function testAlertPipeline(): Promise<{
  notification_created: boolean
  notification_id: number
  telegram: boolean
  discord: boolean
  errors: Record<string, string>
}> {
  const { data } = await api.post('/api/notifications/alerts/test')
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Market Heatmap
// ─────────────────────────────────────────────────────────────────────────────

export async function getMarketHeatmap(
  index = 'all',
  period = '1d',
): Promise<HeatmapData> {
  const { data } = await api.get('/api/market/heatmap', {
    params: { index, period },
    timeout: 120_000,
  })
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Screener
// ─────────────────────────────────────────────────────────────────────────────

/** Kick off a background screener run — returns immediately; poll getScreenerResults. */
export async function runScreener(filters: ScreenerFilters): Promise<ScreenerRunKickoff> {
  const { data } = await api.post('/api/screener/run', filters)
  return data
}

/** Last stored screener result — instant, never triggers a scan. */
export async function getScreenerResults(): Promise<ScreenerStoredResult> {
  const { data } = await api.get('/api/screener/results')
  return data
}

export async function getScreenerSectors(): Promise<{ sectors: string[] }> {
  const { data } = await api.get('/api/screener/sectors')
  return data
}

export async function getScreenerPresets(): Promise<{ presets: ScreenerPreset[] }> {
  const { data } = await api.get('/api/screener/presets')
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Quant Ratings
// ─────────────────────────────────────────────────────────────────────────────

export async function getStockRating(ticker: string): Promise<StockRating> {
  const { data } = await api.get(`/api/ratings/stock/${ticker}`, { timeout: 120_000 })
  return data
}

export async function getTopRated(limit = 20): Promise<StockRatingsResponse> {
  const { data } = await api.get(`/api/ratings/top?limit=${limit}`, { timeout: 120_000 })
  return data
}

export async function screenByRating(minRating?: number, grades?: string[]): Promise<StockRatingsResponse> {
  const params = new URLSearchParams()
  if (minRating != null) params.set('min_rating', String(minRating))
  if (grades?.length) params.set('grades', grades.join(','))
  const { data } = await api.get(`/api/ratings/screen?${params.toString()}`, { timeout: 120_000 })
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Index Trigger Pages
// ─────────────────────────────────────────────────────────────────────────────

export async function getIndexData(index: string): Promise<IndexData> {
  const { data } = await api.get(`/api/indices/${index}`, { timeout: 120_000 })
  return data
}

export async function getMarketSessions(): Promise<MarketSessionsResponse> {
  const { data } = await api.get('/api/indices/sessions')
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Markets Overview (all asset classes)
// ─────────────────────────────────────────────────────────────────────────────

export async function getMarketsOverview(): Promise<import('@/lib/types').MarketsOverviewData> {
  const { data } = await api.get('/api/markets/overview', { timeout: 15_000 })
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Charts
// ─────────────────────────────────────────────────────────────────────────────

export async function getChartOHLCV(
  ticker: string,
  period = '1y',
  interval = '1d',
): Promise<OHLCVResponse> {
  const { data } = await api.get(`/api/charts/ohlcv/${ticker}`, {
    params: { period, interval },
    timeout: 30_000,
  })
  return data
}

export async function getChartTechnicals(
  ticker: string,
  period = '1y',
  interval = '1d',
): Promise<TechnicalsResponse> {
  const { data } = await api.get(`/api/charts/technicals/${ticker}`, {
    params: { period, interval },
    timeout: 30_000,
  })
  return data
}

export async function getChartIndicators(
  ticker: string,
  period = '1y',
): Promise<IndicatorsResponse> {
  const { data } = await api.get(`/api/charts/indicators/${ticker}`, {
    params: { period },
    timeout: 30_000,
  })
  return data
}

export async function getChartCompare(
  tickers: string[],
  period = '1y',
): Promise<CompareResponse> {
  const { data } = await api.get('/api/charts/compare', {
    params: { tickers: tickers.join(','), period },
    timeout: 30_000,
  })
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// News Aggregation
// ─────────────────────────────────────────────────────────────────────────────

export async function getNewsFeed(limit = 50) {
  const { data } = await api.get('/api/news/feed', { params: { limit } })
  return data
}

export async function getNewsForTicker(ticker: string, limit = 20) {
  const { data } = await api.get(`/api/news/ticker/${ticker}`, { params: { limit } })
  return data
}

export async function getBreakingNews() {
  const { data } = await api.get('/api/news/breaking')
  return data
}

export async function getNewsCategories() {
  const { data } = await api.get('/api/news/categories')
  return data
}

export async function getSecFilings(days = 7, formType = 'all') {
  const { data } = await api.get('/api/news/sec-filings', {
    params: { days, form_type: formType },
  })
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Sector Analysis
// ─────────────────────────────────────────────────────────────────────────────

export async function getSectorPerformance(
  period = '1m',
): Promise<SectorPerformanceResponse> {
  const { data } = await api.get('/api/sectors/performance', {
    params: { period },
    timeout: 45_000,
  })
  return data
}

export async function getSectorRotationData(): Promise<SectorRotationResponse> {
  const { data } = await api.get('/api/sectors/rotation', { timeout: 45_000 })
  return data
}

export async function getSectorTopStocks(
  sector: string,
): Promise<SectorTopStocksResponse> {
  const { data } = await api.get(`/api/sectors/${sector}/top-stocks`, {
    timeout: 30_000,
  })
  return data
}

export async function getSectorCorrelation(): Promise<SectorCorrelationResponse> {
  const { data } = await api.get('/api/sectors/correlation', { timeout: 45_000 })
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Performance Reporting
// ─────────────────────────────────────────────────────────────────────────────

export async function getMonthlyReport(month?: string): Promise<MonthlyReport> {
  const { data } = await api.get('/api/reports/monthly', {
    params: month ? { month } : undefined,
  })
  return data
}

export async function getFundSummary(): Promise<FundSummary> {
  const { data } = await api.get('/api/reports/summary')
  return data
}

export async function getPerformanceAttribution(): Promise<PerformanceAttribution> {
  const { data } = await api.get('/api/reports/attribution')
  return data
}

export async function generatePdfReport(month?: string): Promise<{ html: string; month: string }> {
  const { data } = await api.post('/api/reports/generate-pdf', { month })
  return data
}

/**
 * Fetch the self-contained HTML export report (authenticated).
 * Opens the report in a new browser tab; user can Ctrl+P → Save as PDF.
 */
export async function openExportReport(month?: string): Promise<void> {
  const params: Record<string, string> = { format: 'html' }
  if (month) params.month = month
  const { data } = await api.get('/api/reports/export', { params, responseType: 'text' })
  const w = window.open('', '_blank')
  if (w) {
    w.document.open()
    w.document.write(data as string)
    w.document.close()
  }
}

export async function getApiUsage(): Promise<Array<{
  source: string
  calls_1min: number
  calls_1hr: number
  calls_24hr: number
}>> {
  const { data } = await api.get('/api/stats/api-usage')
  return data
}

export async function getPerformanceSeries(days = 90): Promise<import('./types').NavPoint[]> {
  const { data } = await api.get('/api/reports/performance-series', { params: { days } })
  return data
}

export async function getMonthlyReturns(): Promise<import('./types').MonthlyReturnEntry[]> {
  const { data } = await api.get('/api/reports/monthly-returns')
  return data
}

export async function getRollingSharpe(window = 30): Promise<import('./types').RollingSharpePoint[]> {
  const { data } = await api.get('/api/reports/rolling-sharpe', { params: { window } })
  return data
}

export async function getRollingDrawdown(): Promise<import('./types').DrawdownPoint[]> {
  const { data } = await api.get('/api/reports/rolling-drawdown')
  return data
}

export async function getWinLossStreak(): Promise<import('./types').WinLossStreak> {
  const { data } = await api.get('/api/reports/win-loss-streak')
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Trade Journal
// ─────────────────────────────────────────────────────────────────────────────

export async function getJournalEntries(): Promise<JournalEntry[]> {
  const { data } = await api.get('/api/journal')
  return data
}

export async function getJournalEntry(id: number): Promise<JournalEntry> {
  const { data } = await api.get(`/api/journal/${id}`)
  return data
}

export async function createJournalEntry(body: JournalEntryCreate): Promise<JournalEntry> {
  const { data } = await api.post('/api/journal/entry', body)
  return data
}

export async function updateJournalEntry(id: number, body: Partial<JournalEntryCreate>): Promise<JournalEntry> {
  const { data } = await api.put(`/api/journal/${id}`, body)
  return data
}

export async function deleteJournalEntry(id: number): Promise<{ deleted: boolean; id: number }> {
  const { data } = await api.delete(`/api/journal/${id}`)
  return data
}

export async function searchJournalEntries(q: string): Promise<JournalEntry[]> {
  const { data } = await api.get('/api/journal/search', { params: { q } })
  return data
}

export async function getJournalTags(): Promise<JournalTagCount[]> {
  const { data } = await api.get('/api/journal/tags')
  return data
}

export async function getJournalAnalytics(): Promise<JournalAnalytics> {
  const { data } = await api.get('/api/journal/analytics')
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Crypto Dashboard
// ─────────────────────────────────────────────────────────────────────────────

export async function getCryptoDashboard(): Promise<CryptoDashboard> {
  const { data } = await api.get('/api/crypto/dashboard')
  return data
}

export async function getCryptoCoinDetail(coinId: string): Promise<CryptoCoinDetail> {
  const { data } = await api.get(`/api/crypto/coin/${coinId}`)
  return data
}

export async function getCryptoDefi(): Promise<CryptoDefiResponse> {
  const { data } = await api.get('/api/crypto/defi')
  return data
}

export async function getCryptoExchanges(): Promise<CryptoExchangesResponse> {
  const { data } = await api.get('/api/crypto/exchanges')
  return data
}

export async function getCryptoWhaleActivity(): Promise<WhaleActivityResponse> {
  const { data } = await api.get('/api/crypto/whale-activity')
  return data
}

export async function getCryptoCorrelation(): Promise<CryptoCorrelationResponse> {
  const { data } = await api.get('/api/crypto/correlation')
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Macro Economics
// ─────────────────────────────────────────────────────────────────────────────

export async function getMacroDashboard(): Promise<MacroDashboard> {
  const { data } = await api.get('/api/macro/dashboard', { timeout: 30_000 })
  return data
}

export async function getMacroCountry(code: string): Promise<Record<string, unknown>> {
  const { data } = await api.get(`/api/macro/country/${code}`)
  return data
}

export async function getMacroRates(): Promise<MacroRatesResponse> {
  const { data } = await api.get('/api/macro/rates')
  return data
}

export async function getMacroInflation(): Promise<MacroInflationResponse> {
  const { data } = await api.get('/api/macro/inflation')
  return data
}

export async function getMacroLeadingIndicators(): Promise<MacroLeadingIndicators> {
  const { data } = await api.get('/api/macro/leading-indicators')
  return data
}

export async function getMacroMonetaryPolicy(): Promise<MonetaryPolicyResponse> {
  const { data } = await api.get('/api/macro/monetary-policy')
  return data
}

export async function getMacroStreetSentiment(): Promise<StreetSentimentResponse> {
  const { data } = await api.get('/api/macro/street-sentiment')
  return data
}

export async function getMacroEconomicSurprise(): Promise<EconomicSurpriseResponse> {
  const { data } = await api.get('/api/macro/economic-surprise')
  return data
}

export async function getMacroHeatMap(): Promise<MacroHeatMapResponse> {
  const { data } = await api.get('/api/macro/heat-map')
  return data
}

export async function getUKDashboard(): Promise<UKEconomyDashboard> {
  const { data } = await api.get('/api/macro/uk', { timeout: 30_000 })
  return data
}

export async function getUKLeadingIndicators(): Promise<UKLeadingIndicatorsResponse> {
  const { data } = await api.get('/api/macro/uk/leading-indicators', { timeout: 30_000 })
  return data
}

export async function getUKRates(): Promise<UKRatesResponse> {
  const { data } = await api.get('/api/macro/uk/rates', { timeout: 30_000 })
  return data
}

export async function getMacroGlobalRanking(indicator: string): Promise<GlobalRankingResponse> {
  const { data } = await api.get(`/api/macro/global-ranking?indicator=${indicator}`, { timeout: 45_000 })
  return data
}

export async function getMacroG7(): Promise<GroupSnapshotResponse> {
  const { data } = await api.get('/api/macro/g7', { timeout: 45_000 })
  return data
}

export async function getMacroG20(): Promise<GroupSnapshotResponse> {
  const { data } = await api.get('/api/macro/g20', { timeout: 45_000 })
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Portfolio Optimizer
// ─────────────────────────────────────────────────────────────────────────────

export async function optimizeMarkowitz(tickers: string[], period = '2y'): Promise<MarkowitzResult> {
  const { data } = await api.post('/api/optimizer/markowitz', { tickers, period }, { timeout: 120_000 })
  return data
}

export async function optimizeRiskParity(tickers: string[], period = '2y'): Promise<RiskParityResult> {
  const { data } = await api.post('/api/optimizer/risk-parity', { tickers, period }, { timeout: 120_000 })
  return data
}

export async function getEfficientFrontier(tickers: string[], period = '2y', n_points = 20): Promise<FrontierResult> {
  const { data } = await api.post('/api/optimizer/efficient-frontier', { tickers, period, n_points }, { timeout: 120_000 })
  return data
}

export async function getRebalanceTrades(
  current_holdings: Record<string, number>,
  target_weights: Record<string, number>,
  total_value?: number,
): Promise<RebalanceResult> {
  const { data } = await api.post('/api/optimizer/rebalance', { current_holdings, target_weights, total_value })
  return data
}

export async function getCurrentVsOptimal(): Promise<CurrentVsOptimal> {
  const { data } = await api.get('/api/optimizer/current-vs-optimal', { timeout: 120_000 })
  return data
}

// ── AQR EPO (Enhanced Portfolio Optimization) ──────────────────────────────

export interface EPOPortfolioResult {
  weights: Record<string, number>
  expected_return: number
  volatility: number
  sharpe_ratio: number
  hhi_concentration: number
}

export interface EPOResult {
  shrinkage: number
  max_sharpe: EPOPortfolioResult
  unshrunk_max_sharpe: EPOPortfolioResult
  individual_stats: Record<string, { expected_return: number; volatility: number }>
  tickers: string[]
  period: string
  error?: string
}

export interface EPORebalanceResult extends RebalanceResult {
  epo_meta: {
    shrinkage: number
    max_sharpe: EPOPortfolioResult
    unshrunk_max_sharpe: EPOPortfolioResult
  }
  error?: string
}

export async function optimizeEPO(
  tickers: string[],
  shrinkage = 0.5,
  period = '2y',
): Promise<EPOResult> {
  const { data } = await api.post(
    '/api/optimizer/epo',
    { tickers, shrinkage, period },
    { timeout: 120_000 },
  )
  return data
}

export async function rebalanceToEPO(
  current_holdings: Record<string, number>,
  shrinkage = 0.5,
  options: { tickers?: string[]; period?: string; total_value?: number; max_trade_pct?: number } = {},
): Promise<EPORebalanceResult> {
  const { data } = await api.post(
    '/api/optimizer/rebalance-to-epo',
    {
      current_holdings,
      shrinkage,
      tickers: options.tickers,
      period: options.period ?? '2y',
      total_value: options.total_value,
      max_trade_pct: options.max_trade_pct,
    },
    { timeout: 120_000 },
  )
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Stock Detail (deep-dive page)
// ─────────────────────────────────────────────────────────────────────────────

export async function getStockDetail(ticker: string): Promise<import('@/lib/types').StockDetail> {
  const { data } = await api.get(`/api/stock/${ticker.toUpperCase()}`, { timeout: 60_000 })
  return data
}

export async function getStockFinancials(ticker: string): Promise<import('@/lib/types').StockFinancials> {
  const { data } = await api.get(`/api/stock/${ticker.toUpperCase()}/financials`, { timeout: 30_000 })
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Backlog / Kanban
// ─────────────────────────────────────────────────────────────────────────────

// Types imported at top of file

export async function getBacklogTasks(): Promise<BacklogTask[]> {
  const { data } = await api.get('/api/backlog')
  return data
}

export async function createBacklogTask(body: BacklogTaskCreate): Promise<BacklogTask> {
  const { data } = await api.post('/api/backlog', body)
  return data
}

export async function updateBacklogTask(id: number, body: BacklogTaskUpdate): Promise<BacklogTask> {
  const { data } = await api.put(`/api/backlog/${id}`, body)
  return data
}

export async function deleteBacklogTask(id: number): Promise<{ deleted: boolean; id: number }> {
  const { data } = await api.delete(`/api/backlog/${id}`)
  return data
}

export async function moveBacklogTask(id: number, status: BacklogTask['status']): Promise<BacklogTask> {
  const { data } = await api.post(`/api/backlog/${id}/move`, { status })
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Forex
// ─────────────────────────────────────────────────────────────────────────────

// Types imported at top of file

export async function getForexDashboard(): Promise<ForexDashboard> {
  const { data } = await api.get('/api/forex/dashboard')
  return data
}

export async function getForexPairDetail(pair: string): Promise<ForexPairDetail> {
  const { data } = await api.get(`/api/forex/pair/${pair.toUpperCase()}`)
  return data
}

export async function getForexCrossRates(): Promise<ForexCrossRates> {
  const { data } = await api.get('/api/forex/cross-rates')
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Commodities
// ─────────────────────────────────────────────────────────────────────────────

export async function getCommoditiesDashboard(): Promise<CommoditiesDashboard> {
  const { data } = await api.get('/api/commodities/dashboard')
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Options Analysis
// ─────────────────────────────────────────────────────────────────────────────

// Types imported at top of file

export async function getOptionsExpirations(ticker: string): Promise<ExpirationsResponse> {
  const { data } = await api.get(`/api/options/expirations/${ticker.toUpperCase()}`)
  return data
}

export async function getOptionsChain(ticker: string, expiry?: string): Promise<OptionsChainResponse> {
  const { data } = await api.get(`/api/options/chain/${ticker.toUpperCase()}`, {
    params: expiry ? { expiry } : {},
    timeout: 30_000,
  })
  return data
}

export async function getOptionsMaxPain(ticker: string, expiry?: string): Promise<MaxPainResponse> {
  const { data } = await api.get(`/api/options/max-pain/${ticker.toUpperCase()}`, {
    params: expiry ? { expiry } : {},
    timeout: 30_000,
  })
  return data
}

export async function getOptionsUnusual(ticker: string): Promise<UnusualActivityResponse> {
  const { data } = await api.get(`/api/options/unusual/${ticker.toUpperCase()}`, { timeout: 45_000 })
  return data
}

export async function getOptionsIVRank(ticker: string): Promise<IVRankResponse> {
  const { data } = await api.get(`/api/options/iv-rank/${ticker.toUpperCase()}`, { timeout: 30_000 })
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// IPO Tracker & Corporate Actions
// ─────────────────────────────────────────────────────────────────────────────

// Types already imported at top of file

export async function getUpcomingIPOs(): Promise<UpcomingIPO[]> {
  const { data } = await api.get('/api/ipo/upcoming')
  return data
}

export async function getRecentIPOs(days = 30): Promise<RecentIPOResponse> {
  const { data } = await api.get('/api/ipo/recent', { params: { days } })
  // Envelope with as_of/stale/note; tolerate a legacy bare-array response
  if (Array.isArray(data)) {
    return { as_of: null, stale: false, note: null, results: data as RecentIPO[] }
  }
  return {
    as_of: data?.as_of ?? null,
    stale: data?.stale ?? false,
    note: data?.note ?? null,
    results: data?.results ?? [],
  }
}

export async function getIPOPerformance(): Promise<IPOPerformanceResponse> {
  const { data } = await api.get('/api/ipo/performance')
  return data
}

export async function getCorporateSplits(days = 90): Promise<SplitsResponse> {
  const { data } = await api.get('/api/corporate-actions/splits', { params: { days } })
  return data
}

export async function getCorporateDividends(days = 30): Promise<CorporateDividend[]> {
  const { data } = await api.get('/api/corporate-actions/dividends', { params: { days } })
  return data
}

// ---------------------------------------------------------------------------
// Dividend Tracking & Income Forecasting
// ---------------------------------------------------------------------------

export async function getDividendPortfolio(withConfidence = false): Promise<DividendPortfolioResponse> {
  const { data } = await api.get('/api/dividends/portfolio', {
    params: withConfidence ? { with_confidence: true } : undefined,
  })
  return data
}

export async function getDividendCalendar(days = 90): Promise<DividendCalendarResponse> {
  const { data } = await api.get('/api/dividends/calendar', { params: { days } })
  return data
}

export async function getDividendHistory(ticker: string): Promise<DividendHistoryResponse> {
  const { data } = await api.get(`/api/dividends/history/${ticker.toUpperCase()}`)
  return data
}

export async function getDividendAristocrats(): Promise<DividendAristocratsResponse> {
  const { data } = await api.get('/api/dividends/aristocrats')
  return data
}

export async function getDividendTopYield(limit = 20): Promise<TopYieldResponse> {
  const { data } = await api.get('/api/dividends/top-yield', { params: { limit } })
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Correlation Scanner & Pair Trading
// ─────────────────────────────────────────────────────────────────────────────

export async function getCorrelationMatrix(
  tickers: string,
  period = '6m',
): Promise<CorrelationMatrixResult> {
  const { data } = await api.get('/api/correlation/matrix', { params: { tickers, period } })
  return data
}

export async function getCorrelatedPairs(
  universe = 'sp50',
  threshold = 0.85,
  period = '6m',
): Promise<CorrelatedPairsResult> {
  const { data } = await api.get('/api/correlation/pairs', { params: { universe, threshold, period } })
  return data
}

export async function getPairDivergence(
  ticker1: string,
  ticker2: string,
  period = '6m',
): Promise<PairDivergenceResult> {
  const { data } = await api.get('/api/correlation/divergence', { params: { ticker1, ticker2, period } })
  return data
}

export async function getCorrelationSectorMatrix(period = '6m'): Promise<SectorCorrelationResult> {
  const { data } = await api.get('/api/correlation/sector', { params: { period } })
  return data
}

// ── Tax Analysis ──────────────────────────────────────────────────────────────

export async function getTaxWrapperSummary(): Promise<TaxWrapperSummary> {
  const { data } = await api.get('/api/tax/wrapper-summary')
  return data
}

export async function getTaxCgtEstimate(): Promise<CgtEstimate> {
  const { data } = await api.get('/api/tax/cgt-estimate')
  return data
}

export async function getTaxAllowances(): Promise<TaxAllowances> {
  const { data } = await api.get('/api/tax/allowances')
  return data
}

export async function getFeeSummary(): Promise<FeeSummary> {
  const { data } = await api.get('/api/fees/summary')
  return data
}

export async function getFeeTerAnalysis(): Promise<FeeTerAnalysis> {
  const { data } = await api.get('/api/fees/ter-analysis')
  return data
}

export async function getAllowanceStatus(): Promise<AllowanceStatus> {
  const { data } = await api.get('/api/allowances/status')
  return data
}

export async function getCgtPlanner(): Promise<CgtPlanner> {
  const { data } = await api.get('/api/tax/cgt-planner')
  return data
}

export async function getIncomeSummary(): Promise<IncomeSummaryResponse> {
  const { data } = await api.get('/api/income/summary')
  return data
}

// ── Discretionary Rebalancing ─────────────────────────────────────────────────

export async function getDiscretionaryCurrent(): Promise<DiscretionaryCurrentResponse> {
  const { data } = await api.get('/api/rebalance/current')
  return data
}

export async function getDiscretionarySuggestions(): Promise<DiscretionarySuggestionsResponse> {
  const { data } = await api.get('/api/rebalance/suggestions')
  return data
}

export async function postWhatIf(body: WhatIfRequest): Promise<WhatIfResponse> {
  const { data } = await api.post('/api/rebalance/what-if', body)
  return data
}

export async function postPriceShock(
  scenarios: import('@/lib/types').PriceShockItem[],
): Promise<import('@/lib/types').PriceShockResponse> {
  const { data } = await api.post('/api/scenario/price-shock', { scenarios })
  return data
}

export interface FxShockRequestBody {
  gbp_move_pct: number
  per_currency?: Array<{ currency: string; move_pct: number }>
}

export async function postFxShock(
  body: FxShockRequestBody,
): Promise<import('@/lib/types').FxShockResponse> {
  const { data } = await api.post('/api/scenario/fx-shock', body)
  return data
}

export async function getModelPortfolios(): Promise<ModelPortfoliosResponse> {
  const { data } = await api.get('/api/rebalance/model-portfolios')
  return data
}

export async function postTargetAllocation(
  targets: Record<string, number>,
): Promise<TargetAllocationResponse> {
  const { data } = await api.post('/api/rebalance/target-allocation', { targets })
  return data
}

// ── Strategy Engine ───────────────────────────────────────────────────────────

export async function getStrategyRecommendations(): Promise<StrategyRecommendationsResponse> {
  const { data } = await api.get('/api/strategy/recommendations')
  return data
}

export async function getPortfolioHealth(): Promise<PortfolioHealthResponse> {
  const { data } = await api.get('/api/strategy/health-check')
  return data
}

// ── Portfolio vs Model Portfolios ──────────────────────────────────────────

export async function getPortfolioCompareModels(): Promise<ModelComparisonResponse> {
  const { data } = await api.get('/api/portfolio/compare-models')
  return data
}

export async function getPortfolioWhatWould(persona: string): Promise<WhatWouldResponse> {
  const { data } = await api.get(`/api/portfolio/what-would/${persona}`)
  return data
}

export async function getPortfolioWhatWouldAll(): Promise<WhatWouldAllResponse> {
  const { data } = await api.get('/api/portfolio/what-would')
  return data
}

// ── Live P&L ───────────────────────────────────────────────────────────────

export async function getLivePnl(): Promise<LivePnlResponse> {
  const { data } = await api.get('/api/pnl/live')
  return data
}

export interface TopMoverEntry {
  ticker: string
  name: string
  account_name: string
  quantity: number
  value: number
  change_pct: number
  change_value: number
}

export interface TopMoversResponse {
  gainers: TopMoverEntry[]
  losers: TopMoverEntry[]
  has_data: boolean
  updated_at: string
}

export async function getTopMovers(): Promise<TopMoversResponse> {
  const { data } = await api.get('/api/pnl/top-movers')
  return data
}

// ── Upcoming Dividends ───────────────────────────────────────────────────────

export interface UpcomingDividend {
  ticker: string
  name: string
  ex_date: string
  yield_pct: number | null
  est_payment: number | null
}

export async function getUpcomingDividends(): Promise<UpcomingDividend[]> {
  const { data } = await api.get('/api/pnl/upcoming-dividends')
  // Backend may return { items: [...] } or a plain array
  return Array.isArray(data) ? data : (data?.items ?? [])
}

// ── Morning Briefing ────────────────────────────────────────────────────────

export interface BriefingSession {
  name: string
  status: 'OPEN' | 'CLOSED'
  hours: string
}

export interface BriefingMove {
  ticker: string
  name: string
  price: number
  change_pct: number
}

export interface BriefingEvent {
  time: string
  event: string
  country: string
  impact: string
  forecast: string | number | null
  previous: string | number | null
}

export interface BriefingRec {
  ticker: string
  action: string
  confidence: number
  agent_name?: string
  agent_id?: string
  title?: string
}

export interface BookCatalyst {
  ticker: string
  source: string
  category: string
  sentiment: number
  summary: string
  created_at: string | null
}

export interface BriefingData {
  generated_at: string
  date_label: string
  market_sessions: BriefingSession[]
  overnight_moves: BriefingMove[]
  economic_events: BriefingEvent[]
  portfolio: {
    pot_value: number
    daily_pnl: number
    total_pnl: number
    open_positions: number
  }
  top_recommendations: BriefingRec[]
  fear_greed: { value: number; label: string; description: string }
  watchlist_alerts: Record<string, unknown>[]
  book_catalysts?: BookCatalyst[]
  _cached?: boolean
}

/** GET /latest now serves the last STORED brief instantly (never builds
 *  inline). status 'not_run' means nothing generated yet — press Regenerate. */
export interface LatestBriefingResponse extends Partial<BriefingData> {
  status?: 'ready' | 'running' | 'not_run'
  running?: boolean
  as_of?: string | null
  note?: string
}

export async function getMorningBriefing(): Promise<LatestBriefingResponse> {
  const { data } = await api.get('/api/briefing/morning')
  return data
}

export async function getLatestBriefing(): Promise<LatestBriefingResponse> {
  const { data } = await api.get('/api/briefing/latest')
  return data
}

/** Kick a background briefing rebuild — returns immediately; poll getLatestBriefing. */
export async function regenerateBriefing(): Promise<{ running: boolean; started_at: string | null; already_running?: boolean }> {
  const { data } = await api.post('/api/briefing/regenerate')
  return data
}

// ── Smart Portfolio Insights ──────────────────────────────────────────────────

// Matches the backend insights_engine payload exactly (was previously a
// mismatched shape — type/detail/action_label/numeric-priority — so details,
// badges and actions rendered blank).
export interface InsightPlanStep {
  action: string
  name: string
  symbol: string | null
  detail: string
  value_gbp: number
}

export interface PortfolioInsight {
  id: string
  category: string                 // market | tax | risk | advisor | goal | cash
  priority: 'high' | 'medium' | 'low'
  title: string
  message: string
  action: string | null            // terminal command, e.g. "/tax-loss"
  impact_gbp: number               // £ at stake / saveable — primary ranking key
  plan?: InsightPlanStep[]         // concrete one-click trade checklist (may be empty)
  created_at: string
  expires_at?: string
  dismissed?: boolean
  household_id?: string
}

export interface DailyInsightsResponse {
  count: number
  total_impact_gbp?: number        // "money on the table" headline
  insights: PortfolioInsight[]
  error?: string
}

export async function getDailyInsights(): Promise<DailyInsightsResponse> {
  const { data } = await api.get<DailyInsightsResponse>('/api/insights/daily')
  return data
}

export async function refreshInsights(): Promise<DailyInsightsResponse> {
  const { data } = await api.post<DailyInsightsResponse>('/api/insights/refresh')
  return data
}

export interface InsightsNarrative {
  narrative: string
  total_impact_gbp?: number
  error?: string
}

export async function getInsightsNarrative(): Promise<InsightsNarrative> {
  const { data } = await api.get<InsightsNarrative>('/api/insights/narrative')
  return data
}

// Portfolio position review — every held name checked for over/under-weight,
// independent of forecast direction. Powers the Action Center review card.
export interface PortfolioFitReviewPosition {
  ticker: string
  weight_pct: number
  value_gbp: number
  sector: string | null
  action: 'BUY' | 'ADD' | 'HOLD' | 'TRIM' | 'EXIT' | 'AVOID' | ''
  weight_band: string
  target_weight_pct: number
  action_reason: string
  suggested_delta_pct: number | null
  overweight: boolean
}

export interface PortfolioFitReviewResponse {
  has_book: boolean
  book_total_gbp: number
  positions: PortfolioFitReviewPosition[]
  by_action: Record<string, number>
  summary: string[]
  error?: string
}

export async function getPortfolioFitReview(): Promise<PortfolioFitReviewResponse> {
  const { data } = await api.get<PortfolioFitReviewResponse>('/api/portfolio-fit/review')
  return data
}

// Staged actions — a tracked worklist; staging never places an order.
export interface StagedAction {
  id: string
  insight_id: string
  title: string
  category: string
  impact_gbp: number
  plan: InsightPlanStep[]
  action: string | null
  status: string
  staged_at: string
}

export async function stageInsight(insightId: string): Promise<StagedAction> {
  const { data } = await api.post<StagedAction>('/api/insights/stage', { insight_id: insightId })
  return data
}
export async function getStagedActions(): Promise<{ count: number; staged: StagedAction[] }> {
  const { data } = await api.get<{ count: number; staged: StagedAction[] }>('/api/insights/staged')
  return data
}
export async function completeStagedAction(id: string): Promise<void> {
  await api.post(`/api/insights/staged/${id}/complete`)
}
export async function removeStagedAction(id: string): Promise<void> {
  await api.delete(`/api/insights/staged/${id}`)
}
export async function getCompletedActions(): Promise<{ count: number; completed: StagedAction[]; total_impact_gbp: number }> {
  const { data } = await api.get<{ count: number; completed: StagedAction[]; total_impact_gbp: number }>('/api/insights/staged/history')
  return data
}
export async function reopenStagedAction(id: string): Promise<void> {
  await api.post(`/api/insights/staged/${id}/reopen`)
}

// ── Proactive Insights engine (GET /api/insights, dismiss, generate) ──────────

export interface Insight {
  id: string
  category: 'market' | 'tax' | 'risk' | 'opportunity' | 'action' | 'info'
  priority: 'high' | 'medium' | 'low'
  title: string
  message: string
  dismissed?: boolean
  created_at?: string
}

export interface InsightsResponse {
  insights: Insight[]
  count: number
}

export async function getInsights(): Promise<InsightsResponse> {
  const { data } = await api.get<InsightsResponse>('/api/insights')
  return data
}

export async function dismissInsight(id: string): Promise<void> {
  await api.post(`/api/insights/${id}/dismiss`)
}

export async function generateInsights(): Promise<InsightsResponse> {
  const { data } = await api.post<InsightsResponse>('/api/insights/generate')
  return data
}

// ─────────────────────────────────────────────────────────────────────────────
// Chart Annotations
// ─────────────────────────────────────────────────────────────────────────────

export async function getAnnotations(ticker: string): Promise<{
  id: string; ticker: string; date: string; price: number; note: string; color: string; createdAt: string
}[]> {
  const { data } = await api.get<{ annotations: Record<string, unknown>[] }>(`/api/annotations/${ticker}`)
  // Map snake_case created_at → camelCase createdAt for frontend consistency
  return (data.annotations ?? []).map(a => ({
    ...(a as { id: string; ticker: string; date: string; price: number; note: string; color: string }),
    createdAt: a.created_at as string,
  }))
}

export async function createAnnotation(
  ticker: string,
  annotation: { id: string; date: string; price: number; note: string; color?: string },
) {
  return api.post(`/api/annotations/${ticker}`, annotation)
}

export async function deleteAnnotation(ticker: string, id: string) {
  return api.delete(`/api/annotations/${ticker}/${id}`)
}

// ─────────────────────────────────────────────────────────────────────────────
// Life Goals
// ─────────────────────────────────────────────────────────────────────────────

export interface LifeGoal {
  id: number
  name: string
  category: string
  target_amount: number
  target_amount_gbp?: number
  current_saved: number
  current_saved_gbp?: number
  target_date: string
  monthly_savings_needed?: number | null
  monthly_savings_needed_gbp?: number | null
  progress_pct?: number
  remaining_gbp?: number
  priority: 'HIGH' | 'MEDIUM' | 'LOW'
  funding_source?: string
  status: 'ACTIVE' | 'ACHIEVED' | 'PAUSED' | 'CANCELLED'
  notes?: string | null
  created_at?: string
  updated_at?: string
}

export type LifeGoalCreate = Omit<LifeGoal, 'id' | 'created_at' | 'updated_at'>
export type LifeGoalUpdate = Partial<LifeGoalCreate>

export async function getLifeGoals(): Promise<LifeGoal[]> {
  const { data } = await api.get<LifeGoal[]>('/api/life-goals')
  // Normalize backend _gbp suffix fields to match frontend expectations
  return (data || []).map(g => ({
    ...g,
    target_amount: g.target_amount ?? g.target_amount_gbp ?? 0,
    current_saved: g.current_saved ?? g.current_saved_gbp ?? 0,
  }))
}

export async function createLifeGoal(body: LifeGoalCreate): Promise<LifeGoal> {
  const { data } = await api.post<LifeGoal>('/api/life-goals', body)
  return data
}

export async function updateLifeGoal(id: number, body: LifeGoalUpdate): Promise<LifeGoal> {
  const { data } = await api.put<LifeGoal>(`/api/life-goals/${id}`, body)
  return data
}

export async function deleteLifeGoal(id: number): Promise<void> {
  await api.delete(`/api/life-goals/${id}`)
}

// ─────────────────────────────────────────────────────────────────────────────
// Advisor Scorecard
// ─────────────────────────────────────────────────────────────────────────────

export interface AdvisorScorecard {
  advisor_name: string
  win_rate: number
  avg_return: number | null
  total_recommendations: number
  best_call: number | null
}

export interface AdvisorRecommendation {
  id: number
  advisor_name: string
  ticker: string
  action: 'BUY' | 'SELL' | 'HOLD' | 'ADD' | 'REDUCE'
  rationale?: string | null
  entry_price?: number | null
  exit_price?: number | null
  outcome_pct?: number | null
  status: 'WIN' | 'LOSS' | 'OPEN'
  date: string
  created_at?: string
}

export type AdvisorRecommendationCreate = Pick<
  AdvisorRecommendation,
  'advisor_name' | 'ticker' | 'action' | 'date'
> & {
  rationale?: string
  entry_price?: number
}

export async function getAdvisorScorecard(): Promise<AdvisorScorecard[]> {
  const { data } = await api.get<AdvisorScorecard[]>('/api/advisor/scorecard')
  return data
}

export async function getAdvisorRecommendations(): Promise<AdvisorRecommendation[]> {
  const { data } = await api.get<AdvisorRecommendation[]>('/api/advisor/recommendations')
  return data
}

export async function createAdvisorRecommendation(
  body: AdvisorRecommendationCreate,
): Promise<AdvisorRecommendation> {
  const { data } = await api.post<AdvisorRecommendation>('/api/advisor/recommendation', body)
  return data
}

// ── Financial Levers ──────────────────────────────────────────────────────────

export interface SaleInProgressCosts {
  agent_fee_pct: number
  agent_fee_vat_pct: number
  agent_fee_gbp: number
  conveyancing_gbp: number
  total_gbp: number
  is_estimate: boolean
  note?: string
}

export interface SaleInProgress {
  status: 'sale_agreed'
  sale_agreed_price_gbp: number
  agreed_date: string | null
  expected_completion: string | null
  sale_costs: SaleInProgressCosts
  cgt: { cgt_due_gbp: number; cgt_rate_pct: number; taxable_gain_gbp: number } | null
  cgt_known: boolean
  cgt_caveat: string | null
  valuation_gbp: number
  valuation_label: string
  mortgage_outstanding_gbp: number
  cash_after_mortgage_gbp: number
  sentence: string
}

export interface SaleInProgressPatch {
  sale_agreed_price_gbp?: number
  agent_fee_pct?: number
  agent_fee_vat_pct?: number
  conveyancing_gbp?: number
  expected_completion?: string | null
  joint_ownership?: boolean
}

export async function patchPropertySaleInProgress(
  slug: string,
  patch: SaleInProgressPatch,
): Promise<{ slug: string; sale_in_progress: SaleInProgress | null }> {
  const { data } = await api.patch(`/api/property/sale-in-progress/${slug}`, patch)
  return data
}

export async function completePropertySale(
  slug: string,
): Promise<{ slug: string; status: string; cash_gbp: number }> {
  const { data } = await api.post(`/api/property/sale-in-progress/${slug}/complete`)
  return data
}

export interface FinancialLever {
  id: string
  title: string
  description: string
  impact_gbp: number
  impact_years: number | null
  category: 'property' | 'tax' | 'savings' | 'investment' | 'pension' | 'risk'
  difficulty: 'low' | 'medium' | 'hard'
  icon: string
  /** Present when a REAL sale is agreed on this property — the lever then
   *  shows agreed figures instead of a hypothetical slider. */
  status?: 'sale_agreed'
  sale_in_progress?: SaleInProgress | null
  property_slug?: string
  /** The first step — a real page, so a life-changing lever is never a
   *  dead-end with a tickbox. */
  link?: string | null
  link_label?: string | null
}

export interface FinancialLeversResponse {
  levers: FinancialLever[]
  all_levers: FinancialLever[]
  total_impact_gbp: number
  lever_count: number
}

export async function getFinancialLevers(): Promise<FinancialLeversResponse> {
  const { data } = await api.get<FinancialLeversResponse>('/api/planning/levers')
  return data
}

export interface LeverSimulationResponse {
  applied_levers: { id: string; title: string; impact_gbp: number }[]
  baseline: {
    pot_today_gbp: number
    annual_savings_gbp: number
    annual_spend_gbp: number
    fire_target_gbp: number
    years_to_fire: number | null
    fire_age: number | null
  }
  scenario: {
    pot_today_gbp: number
    annual_savings_gbp: number
    annual_spend_gbp: number
    fire_target_gbp: number
    years_to_fire: number | null
    fire_age: number | null
  }
  deltas: {
    extra_lump_gbp: number
    extra_annual_savings_gbp: number
    annual_drawdown_reduction_gbp: number
    years_saved: number
    fire_target_reduction_gbp: number
  }
}

export async function simulateLevers(
  leverIds: string[],
  leverTiming?: Record<string, number>,
): Promise<LeverSimulationResponse> {
  const { data } = await api.post<LeverSimulationResponse>(
    '/api/planning/levers/simulate',
    {
      lever_ids: leverIds,
      lever_timing: leverTiming,
    },
  )
  return data
}

export async function updateHouseholdProfile(updates: {
  current_age?: number
  retirement_age?: number
  target_drawdown_gbp?: number
  annual_savings_gbp?: number
  estimated_annual_spend_gbp?: number
  planning_assumptions?: {
    sell_farm_avenue?: boolean
    farm_avenue_sale_year?: number
    farm_avenue_net_proceeds?: number
    sell_gables?: boolean
    gables_sale_year?: number | null
    planned_jurisdiction?: string
    include_property_in_fire?: boolean
    sipp_drawdown_start_age?: number
    db_pension_retirement_age?: number
  }
}): Promise<void> {
  await api.put('/api/profile', { household: updates })
}

// ── Phase A: Shared Planning Assumptions ──────────────────────────────────────
//
// Backed by `/api/profile/assumptions`. Single source of truth that all
// planning pages (MC / FIRE / drawdown / scenarios) read defaults from
// and write back to via the `usePlanningAssumptions` hook.

import type { PlanningAssumptions, PlanningAssumptionsPatch } from './types'

export async function getPlanningAssumptions(): Promise<PlanningAssumptions> {
  const res = await api.get<PlanningAssumptions>('/api/profile/assumptions')
  return res.data
}

export async function patchPlanningAssumptions(
  patch: PlanningAssumptionsPatch,
): Promise<PlanningAssumptions> {
  const res = await api.patch<PlanningAssumptions>('/api/profile/assumptions', patch)
  return res.data
}

// ── Phase B: capability + disclaimer + broker-keys API ────────────────────────

import type {
  Capabilities,
  DisclaimerStatus,
  DisclaimerCopy,
  BrokerKeysList,
  BrokerKeyMeta,
} from './types'

export async function getCapabilities(): Promise<Capabilities> {
  const res = await api.get<Capabilities>('/api/account/me/capabilities')
  return res.data
}

export async function getDisclaimerStatus(): Promise<DisclaimerStatus> {
  const res = await api.get<DisclaimerStatus>('/api/disclaimers/status')
  return res.data
}

export async function getDisclaimerCopy(): Promise<DisclaimerCopy> {
  const res = await api.get<DisclaimerCopy>('/api/disclaimers/copy')
  return res.data
}

export async function acceptDisclaimer(metadata?: {
  client_user_agent?: string
  client_locale?: string
}): Promise<{ ok: boolean }> {
  const res = await api.post<{ ok: boolean }>('/api/disclaimers/accept', metadata ?? {})
  return res.data
}

export async function listBrokerKeys(): Promise<BrokerKeysList> {
  const res = await api.get<BrokerKeysList>('/api/account/me/broker-keys')
  return res.data
}

export async function upsertBrokerKey(body: {
  broker_name: string
  api_key: string
  api_secret: string
  label?: string
  is_paper?: boolean
  expires_at?: string
}): Promise<BrokerKeyMeta> {
  const res = await api.post<BrokerKeyMeta>('/api/account/me/broker-keys', body)
  return res.data
}

export async function deleteBrokerKey(brokerName: string): Promise<void> {
  await api.delete(`/api/account/me/broker-keys/${encodeURIComponent(brokerName)}`)
}

// Phase C — enriched property-plan list (gross + mortgage + equity)
import type { PropertySalePlanEnriched } from './types'
export async function getPropertyPlans(): Promise<{ plans: PropertySalePlanEnriched[] }> {
  const res = await api.get<{ plans: PropertySalePlanEnriched[] }>('/api/property/plans')
  return res.data
}

// ── Phase B — auto-execution consent ────────────────────────────────────────

import type {
  AutoExecutionConsent,
  AutoExecutionConsentUpsert,
} from './types'

export async function getAutoExecutionConsent(): Promise<AutoExecutionConsent> {
  const res = await api.get<AutoExecutionConsent>('/api/account/me/consent')
  return res.data
}

export async function upsertAutoExecutionConsent(
  body: AutoExecutionConsentUpsert,
): Promise<AutoExecutionConsent> {
  const res = await api.post<AutoExecutionConsent>('/api/account/me/consent', body)
  return res.data
}

export async function setKillSwitch(active: boolean): Promise<AutoExecutionConsent> {
  const res = await api.post<AutoExecutionConsent>('/api/account/me/consent/kill-switch', { active })
  return res.data
}

export async function renewAutoExecutionConsent(): Promise<AutoExecutionConsent> {
  const res = await api.post<AutoExecutionConsent>('/api/account/me/consent/renew', {})
  return res.data
}

export async function deleteAutoExecutionConsent(): Promise<AutoExecutionConsent> {
  const res = await api.delete<AutoExecutionConsent>('/api/account/me/consent')
  return res.data
}

// ── Cost-Basis Drift ──────────────────────────────────────────────────────────

export async function getCostBasisDrift(): Promise<import('./types').CostBasisDriftResponse> {
  const res = await api.get<import('./types').CostBasisDriftResponse>('/api/audit/cost-basis-drift')
  return res.data
}

// ── Holdings Data-Integrity Score ─────────────────────────────────────────────

export async function getDataIntegrityScore(): Promise<import('./types').DataIntegrityScoreResponse> {
  const res = await api.get<import('./types').DataIntegrityScoreResponse>('/api/audit/data-integrity-score')
  return res.data
}

// ── Household Financial-Resilience Score ─────────────────────────────────────

export async function getResilienceScore(): Promise<import('./types').ResilienceScoreResponse> {
  const res = await api.get<import('./types').ResilienceScoreResponse>('/api/resilience/score')
  return res.data
}
